<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: { type: [String, null], default: null },
  options: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
  triggerPlaceholder: { type: String, default: 'בחרו' },
  title: { type: String, default: 'בחירה' },
  searchPlaceholder: { type: String, default: 'הקלידו לחיפוש...' },
  loadingText: { type: String, default: 'טוען...' },
  noResultsText: { type: String, default: 'לא נמצאו תוצאות' },
  maxResults: { type: Number, default: 50 },
  // Allow committing whatever the user typed, even if it's not in `options`.
  allowFreeText: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const search = ref('')
const highlightedIndex = ref(-1)
const searchInput = ref(null)
const listEl = ref(null)

const trimmedSearch = computed(() => search.value.trim())

const filtered = computed(() => {
  const q = trimmedSearch.value
  if (!q) return props.options.slice(0, props.maxResults)
  return props.options.filter((o) => o.includes(q)).slice(0, props.maxResults)
})

// When free text is allowed, offer a "use what I typed" row — unless the typed
// value already matches an option exactly (it's then shown in the list anyway).
const showFreeText = computed(
  () =>
    props.allowFreeText &&
    trimmedSearch.value.length > 0 &&
    !props.options.includes(trimmedSearch.value)
)

// ------------------------------------------------------------------
// Visual-viewport sizing.
// In an in-app browser (Facebook / Instagram WebView) `100vh` / `100dvh`
// are unreliable and the on-screen keyboard overlays the layout. We size
// the overlay to `window.visualViewport` (the area NOT covered by the
// keyboard), so the search box stays pinned at the top and the results
// list always renders above the keyboard.
// ------------------------------------------------------------------
const backdropStyle = ref(null)

const applyViewport = () => {
  const compact = !!window.matchMedia?.('(max-width: 767px)')?.matches
  if (!compact) {
    // Desktop renders as a centered modal — let CSS handle sizing.
    backdropStyle.value = null
    return
  }
  const vv = window.visualViewport
  const height = vv ? vv.height : window.innerHeight
  const top = vv ? vv.offsetTop : 0
  backdropStyle.value = { top: `${top}px`, height: `${height}px`, bottom: 'auto' }
}

let vvBound = false
const bindViewport = () => {
  const vv = window.visualViewport
  if (vv && !vvBound) {
    vv.addEventListener('resize', applyViewport)
    vv.addEventListener('scroll', applyViewport)
    vvBound = true
  }
  applyViewport()
}
const unbindViewport = () => {
  const vv = window.visualViewport
  if (vv && vvBound) {
    vv.removeEventListener('resize', applyViewport)
    vv.removeEventListener('scroll', applyViewport)
    vvBound = false
  }
}

const open = () => {
  if (props.disabled) return
  isOpen.value = true
  // Prefill with the current value when free text is allowed, so the user can
  // refine what they typed rather than starting over.
  search.value = props.allowFreeText && props.modelValue ? props.modelValue : ''
  highlightedIndex.value = -1
  document.body.style.overflow = 'hidden'
  bindViewport()
  nextTick(() => {
    searchInput.value?.focus()
  })
}

const close = () => {
  isOpen.value = false
  document.body.style.overflow = ''
  unbindViewport()
}

const select = (option) => {
  emit('update:modelValue', option)
  close()
}

const scrollHighlightedIntoView = () => {
  nextTick(() => {
    const el = listEl.value?.querySelector('.picker-option.highlighted')
    el?.scrollIntoView({ block: 'nearest' })
  })
}

const onKeydown = (e) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlightedIndex.value = Math.min(highlightedIndex.value + 1, filtered.value.length - 1)
    scrollHighlightedIntoView()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
    scrollHighlightedIntoView()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (highlightedIndex.value >= 0 && filtered.value[highlightedIndex.value]) {
      select(filtered.value[highlightedIndex.value])
    } else if (props.allowFreeText && trimmedSearch.value) {
      // "Go"/Enter commits the typed value as-is.
      select(trimmedSearch.value)
    }
  } else if (e.key === 'Escape') {
    close()
  }
}

// Reset highlight as the query changes.
watch(search, () => {
  highlightedIndex.value = -1
})

onUnmounted(() => {
  document.body.style.overflow = ''
  unbindViewport()
})
</script>

<template>
  <div class="picker-field">
    <!-- Closed trigger (looks like a regular input) -->
    <button
      type="button"
      class="picker-trigger"
      :class="{
        'has-error': hasError,
        'is-placeholder': !modelValue,
        'is-disabled': disabled,
      }"
      :disabled="disabled"
      @click="open"
    >
      <svg
        class="picker-trigger-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
      <span class="picker-trigger-text">{{
        loading ? loadingText : modelValue || triggerPlaceholder
      }}</span>
      <svg
        class="picker-trigger-chevron"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>

    <!-- Full-screen picker overlay -->
    <Teleport to="body">
      <Transition name="picker">
        <div v-if="isOpen" class="picker-backdrop" :style="backdropStyle" @click.self="close">
          <div class="picker-panel">
            <!-- Header -->
            <div class="picker-header">
              <h3 class="picker-title">{{ title }}</h3>
              <button type="button" class="picker-close" aria-label="סגירה" @click="close">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Search (pinned at the top, above the keyboard) -->
            <div class="picker-search">
              <svg
                class="picker-search-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref="searchInput"
                v-model="search"
                type="text"
                class="picker-search-input"
                :placeholder="searchPlaceholder"
                autocomplete="off"
                @keydown="onKeydown"
              />
            </div>

            <!-- Results (scrolls between the search box and the keyboard) -->
            <div ref="listEl" class="picker-list">
              <div v-if="loading" class="picker-status">{{ loadingText }}</div>
              <template v-else>
                <!-- Use whatever the user typed -->
                <button
                  v-if="showFreeText"
                  type="button"
                  class="picker-option picker-option--free"
                  @click="select(trimmedSearch)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span
                    >השתמשו במה שהוקלד: <strong>{{ trimmedSearch }}</strong></span
                  >
                </button>

                <button
                  v-for="(option, index) in filtered"
                  :key="option"
                  type="button"
                  class="picker-option"
                  :class="{
                    highlighted: index === highlightedIndex,
                    selected: option === modelValue,
                  }"
                  @click="select(option)"
                >
                  {{ option }}
                </button>

                <div v-if="!showFreeText && filtered.length === 0" class="picker-status">
                  {{ noResultsText }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.picker-field {
  width: 100%;
}

/* ---- Closed trigger ---- */
.picker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  /* 16px keeps iOS from auto-zooming the page when fields gain focus. */
  font-size: 16px;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  background: white;
  color: var(--text-primary);
  cursor: pointer;
  text-align: right;
  transition: all var(--transition-fast);
}

.picker-trigger.is-placeholder .picker-trigger-text {
  color: var(--text-muted);
}

.picker-trigger:focus-visible {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
}

.picker-trigger.has-error {
  border-color: #dc2626;
  background: rgba(220, 38, 38, 0.02);
}

.picker-trigger.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.picker-trigger-icon {
  stroke: var(--text-muted);
  flex-shrink: 0;
}

.picker-trigger-text {
  flex: 1;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-trigger-chevron {
  stroke: var(--text-muted);
  flex-shrink: 0;
}

/* ---- Overlay ---- */
.picker-backdrop {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  flex-direction: column;
}

.picker-panel {
  position: relative;
  background: white;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.picker-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.picker-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: var(--bg-secondary);
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.picker-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.picker-search {
  position: relative;
  padding: var(--spacing-md) var(--spacing-lg);
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-color);
}

.picker-search-icon {
  position: absolute;
  right: calc(var(--spacing-lg) + var(--spacing-sm));
  top: 50%;
  transform: translateY(-50%);
  stroke: var(--text-muted);
  pointer-events: none;
}

.picker-search-input {
  width: 100%;
  padding: var(--spacing-md) calc(var(--spacing-lg) + 28px) var(--spacing-md) var(--spacing-lg);
  font-size: 16px;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  background: white;
  color: var(--text-primary);
  text-align: right;
  direction: rtl;
}

.picker-search-input:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
}

.picker-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: var(--spacing-xs) 0;
}

.picker-option {
  display: block;
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
  color: var(--text-primary);
  background: transparent;
  border: none;
  text-align: right;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.picker-option:hover,
.picker-option.highlighted {
  background: rgba(34, 197, 94, 0.08);
}

.picker-option.selected {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
  font-weight: var(--font-weight-semibold);
}

/* "Use what I typed" row */
.picker-option--free {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: #16a34a;
  font-weight: var(--font-weight-medium);
  border-bottom: 1px solid var(--border-color);
}

.picker-option--free svg {
  flex-shrink: 0;
  stroke: #16a34a;
}

.picker-option--free strong {
  font-weight: var(--font-weight-bold);
  word-break: break-word;
}

.picker-status {
  padding: var(--spacing-xl) var(--spacing-lg);
  text-align: center;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* Desktop: centered modal instead of full-screen sheet */
@media (min-width: 768px) {
  .picker-backdrop {
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    padding: var(--spacing-lg);
  }

  .picker-panel {
    flex: none;
    width: 100%;
    max-width: 480px;
    height: min(640px, 85vh);
    border-radius: var(--border-radius-xl);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  }
}

/* ---- Transitions ---- */
.picker-enter-active,
.picker-leave-active {
  transition: opacity 0.2s ease;
}

.picker-enter-from,
.picker-leave-to {
  opacity: 0;
}

.picker-enter-active .picker-panel,
.picker-leave-active .picker-panel {
  transition: transform 0.25s ease;
}

.picker-enter-from .picker-panel,
.picker-leave-to .picker-panel {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .picker-enter-from .picker-panel,
  .picker-leave-to .picker-panel {
    transform: scale(0.95);
  }
}
</style>
